import React, { useEffect, useState } from 'react'

/**
 * Shared primitives for the new Rule Editor — metric types, chip components,
 * operator picker, value input, and bid mode select.
 *
 * Self-contained Tailwind, no Blueprint imports. Consumed by both the
 * production PromotionRuleEditorNew component and the Storybook stories.
 */

// ────────────────────────────────────────────────────────────────────────────
// Shared metric metadata
// ────────────────────────────────────────────────────────────────────────────

export type Op = '≥' | '≤' | '=' | '>' | '<'
export type MetricKey =
    | 'orders'
    | 'spend'
    | 'acos'
    | 'clicks'
    | 'blended_profit'
    | 'adjusted_orders'
    | 'blended_acos'
    | 'adjusted_pages_read'
    | 'adjusted_estimated_royalties'

interface Metric {
    key: MetricKey
    label: string
    prefix?: string
    suffix?: string
    currencyPrefix?: boolean
    defaultOp: Op
}

export interface MetricWithIcon extends Metric {
    icon: string // single-char glyph stand-in; swap for real icon set later
    description: string
    /** When true, only shown on KDP profiles. */
    kdpOnly?: boolean
}

export const METRICS: MetricWithIcon[] = [
    {
        key: 'orders',
        label: 'Orders',
        defaultOp: '≥',
        icon: '#',
        description: 'Orders attributed to the search term',
    },
    {
        key: 'spend',
        label: 'Spend',
        currencyPrefix: true,
        defaultOp: '≥',
        icon: '$',
        description: 'Ad spend on the search term',
    },
    {
        key: 'acos',
        label: 'ACOS',
        suffix: '%',
        defaultOp: '≤',
        icon: '%',
        description: 'Advertising cost of sale',
    },
    {
        key: 'clicks',
        label: 'Clicks',
        defaultOp: '≥',
        icon: '↗',
        description: 'Number of clicks on ads',
    },
    {
        key: 'blended_profit',
        label: 'Blended Profit',
        currencyPrefix: true,
        defaultOp: '≥',
        icon: '$',
        description: 'Profit across organic and ad sales',
        kdpOnly: true,
    },
    {
        key: 'adjusted_orders',
        label: 'Adjusted Orders',
        defaultOp: '≥',
        icon: '#',
        description: 'Orders after attribution adjustments',
        kdpOnly: true,
    },
    {
        key: 'blended_acos',
        label: 'Blended ACOS',
        suffix: '%',
        defaultOp: '≤',
        icon: '%',
        description: 'Blended advertising cost of sale',
        kdpOnly: true,
    },
    {
        key: 'adjusted_pages_read',
        label: 'Adj. Pages Read',
        defaultOp: '≥',
        icon: '#',
        description: 'Adjusted Kindle pages read',
        kdpOnly: true,
    },
    {
        key: 'adjusted_estimated_royalties',
        label: 'Adj. Est. Royalties',
        currencyPrefix: true,
        defaultOp: '≥',
        icon: '$',
        description: 'Adjusted estimated royalties',
        kdpOnly: true,
    },
]

export function MetricChip({
    icon,
    label,
    active = false,
    hideIcon = false,
    onClick,
}: {
    icon: string
    label: string
    active?: boolean
    hideIcon?: boolean
    onClick?: () => void
}) {
    const ringCls = active
        ? 'ring-[#3391FF]/40 ring-2 bg-[#F1F3F6]'
        : 'ring-[#DBE2E8] hover:bg-[#F1F3F6]'
    return (
        <button
            type="button"
            onClick={onClick}
            className={
                'inline-flex items-center gap-1.5 h-7 rounded-md bg-[#FAFBFC] ring-1 text-[14px] font-[600] tracking-[-0.01em] text-[#0E2947] transition cursor-pointer whitespace-nowrap min-w-[140px] ' +
                (hideIcon ? 'pl-2.5 pr-1.5 ' : 'pl-1 pr-1.5 ') +
                ringCls
            }
        >
            {!hideIcon && (
                <span className="size-5 rounded bg-[#EAEDF1] text-[#7289A3] text-[12px] font-medium inline-flex items-center justify-center shrink-0">
                    {icon}
                </span>
            )}
            <span className="truncate">{label}</span>
            <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                className="text-[#899CB3] ml-auto shrink-0"
            >
                <path
                    d="M2.5 4l2.5 2.5L7.5 4"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    )
}

export function MetricPickerMenu({
    query,
    onQueryChange,
    metrics,
    currentKey,
    onPick,
    onClose,
}: {
    query: string
    onQueryChange: (v: string) => void
    metrics: MetricWithIcon[]
    currentKey?: MetricKey
    onPick: (m: MetricWithIcon) => void
    onClose: () => void
}) {
    const ref = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
        function onDown(e: MouseEvent) {
            if (!ref.current) return
            if (!ref.current.contains(e.target as Node)) onClose()
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [onClose])

    return (
        <div
            ref={ref}
            className="bg-white rounded-lg ring-1 ring-[#DBE2E8] shadow-[0_8px_24px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.04)] w-52 overflow-hidden"
        >
            <div className="px-3 py-2 border-b border-[#EAEDF1]">
                <input
                    autoFocus
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    placeholder="Search metrics…"
                    className="w-full bg-transparent outline-none text-[14px] placeholder:text-[#899CB3]"
                />
            </div>
            <div className="max-h-60 overflow-y-auto py-1">
                {metrics.length === 0 ? (
                    <div className="px-3 py-2 text-[13px] text-[#899CB3]">No metrics found</div>
                ) : (
                    metrics.map((m) => {
                        const isCurrent = m.key === currentKey
                        return (
                            <button
                                key={m.key}
                                onClick={() => onPick(m)}
                                className={
                                    'w-full text-left px-3 py-1.5 flex items-center justify-between gap-2 text-[14px] hover:bg-[#F1F3F6] ' +
                                    (isCurrent ? 'text-[#0E2947] font-medium' : 'text-[#0E2947]')
                                }
                            >
                                {m.label}
                                {isCurrent && (
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        className="text-[#3391FF] shrink-0"
                                    >
                                        <path
                                            d="M2.5 6.5l2.5 2.5 4.5-5"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </button>
                        )
                    })
                )}
            </div>
        </div>
    )
}

export function OperatorPill({ value, onChange }: { value: Op; onChange: (v: Op) => void }) {
    const OPS: { value: Op; hint: string }[] = [
        { value: '≥', hint: 'is at least' },
        { value: '>', hint: 'is more than' },
        { value: '≤', hint: 'is at most' },
        { value: '<', hint: 'is less than' },
        { value: '=', hint: 'equals' },
    ]
    const [open, setOpen] = useState(false)
    const ref = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
        if (!open) return
        function onDown(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    return (
        <div className="relative inline-flex" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="Operator"
                aria-haspopup="listbox"
                aria-expanded={open}
                className={
                    'inline-flex items-center justify-center gap-0.5 h-7 w-[38px] pl-2 pr-1 rounded-md bg-[#FAFBFC] ring-1 text-[15px] text-[#0E2947] font-[700] tracking-[-0.01em] tabular-nums cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#3391FF]/40 transition ' +
                    (open
                        ? 'ring-2 ring-[#3391FF]/40 bg-[#F1F3F6]'
                        : 'ring-[#DBE2E8] hover:bg-[#F1F3F6]')
                }
            >
                <span className="flex-1 text-center">{value}</span>
                <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    className="text-[#899CB3] shrink-0"
                >
                    <path
                        d="M2.5 4l2.5 2.5L7.5 4"
                        stroke="currentColor"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-1 z-20 bg-white rounded-lg ring-1 ring-[#DBE2E8] shadow-[0_8px_24px_rgba(14,41,71,0.10),0_2px_4px_rgba(14,41,71,0.05)] w-48 overflow-hidden py-1">
                    {OPS.map((o) => {
                        const selected = o.value === value
                        return (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => {
                                    onChange(o.value)
                                    setOpen(false)
                                }}
                                className="w-full text-left px-3 py-1.5 hover:bg-[#F1F3F6] flex items-center gap-2.5"
                            >
                                <span className="size-5 inline-flex items-center justify-center text-[#0E2947] text-[14px] font-medium tabular-nums">
                                    {o.value}
                                </span>
                                <span className="flex-1 text-[13px] text-[#7289A3]">{o.hint}</span>
                                {selected && (
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        className="text-[#0E2947] shrink-0"
                                    >
                                        <path
                                            d="M2.5 6.5l2.5 2.5 4.5-5"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export function ValueChip({
    value,
    onChange,
    prefix,
    suffix,
    placeholder,
    withBorder,
}: {
    value: string
    onChange: (v: string) => void
    prefix?: string
    suffix?: string
    placeholder?: string
    withBorder?: boolean
}) {
    return (
        <span
            className={
                'inline-flex items-center w-[72px] h-7 px-2 rounded-md bg-[#FAFBFC] text-[15px] font-[650] tracking-[-0.02em] tabular-nums text-[#0E2947] gap-1 transition' +
                (withBorder ? ' ring-1 ring-[#DBE2E8]' : '')
            }
        >
            {prefix && <span className="text-[#899CB3] tabular-nums shrink-0">{prefix}</span>}
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder ?? 'value'}
                className="flex-1 min-w-0 bg-transparent outline-none border-0 text-[#0E2947] placeholder:text-[#899CB3] tabular-nums font-medium text-right"
            />
            {suffix && <span className="text-[#899CB3] shrink-0">{suffix}</span>}
        </span>
    )
}

export function ModeSelect<T extends string>({
    value,
    onChange,
    options,
}: {
    value: T
    onChange: (v: T) => void
    options: { value: T; label: string }[]
}) {
    return (
        <div className="relative inline-flex">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value as T)}
                className="appearance-none h-7 pl-2.5 pr-7 rounded-md bg-[#FAFBFC] ring-1 ring-[#DBE2E8] text-[14px] text-[#0E2947] font-medium hover:bg-[#F1F3F6] cursor-pointer outline-none focus-visible:ring-[#3391FF]/40 focus-visible:ring-2 transition"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
            <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#899CB3]"
            >
                <path
                    d="M2.5 4l2.5 2.5L7.5 4"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    )
}
